import { Scroller, Box, Stack } from '@cast/design-system';

import { TourStep } from 'core/tour';
import { CostRateSelect } from 'features/cost-report/components/cost-over-time/CostRateSelect';

import { NormalizedCostSectionControls } from './_components/NormalizedCostSectionControls';
import { CpuCostChart } from './CpuCostChart';
import { GpuCostChart } from './GpuCostChart';
import { MemoryCostChart } from './MemoryCostChart';
import { NormalizedCostProvider } from './NormalizedCostProvider';
import { StorageCostChart } from './StorageCostChart';
import { CostReportChartToggle } from '../../../../cost-report/components/CostReportChartToggle';
import { ReportSection } from '../../../../cost-report/components/ReportSection';
import { useCostReportContext } from '../../../../cost-report/hooks/useCostReportContext';
import { isStorageReportsSupported } from '../../../utils';
import { costOverTimeTourId, tourSteps } from '../../tourSteps';

import { useCluster } from '@cast/shared-cluster-context-data-access';

type Props = { costType?: 'provisioned' | 'requested' };

export const NormalizedCostSection = ({ costType = 'provisioned' }: Props) => {
  const { hasGpuData } = useCostReportContext();

  const { cluster } = useCluster();
  const showStorage = isStorageReportsSupported(cluster);

  let cardsCount = 2;
  if (hasGpuData) {
    cardsCount++;
  }
  if (showStorage) {
    cardsCount++;
  }

  const layout = cardsCount > 3 ? 'grid' : 'row';

  return (
    <NormalizedCostProvider>
      <ReportSection testId="normalized-cost-section">
        <TourStep
          step={tourSteps.normalizedCostChart}
          type={costOverTimeTourId}
          text="Check the normalized cost per provisioned resource to understand how your costs are fluctuating."
          highlight="shadow"
          placement="top"
        >
          <Stack
            direction="row"
            flexWrap="nowrap"
            alignItems="flex-end"
            gap={24}
            mb={16}
          >
            <NormalizedCostSectionControls />
            <Stack
              direction="row"
              flexWrap="nowrap"
              alignItems="flex-end"
              gap="8px"
            >
              <CostRateSelect />
              <CostReportChartToggle />
            </Stack>
          </Stack>
          <Scroller>
            <Box
              sx={
                layout === 'row'
                  ? {
                      display: 'flex',
                      flexDirection: 'row',
                      flexWrap: 'nowrap',
                    }
                  : {
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                    }
              }
              gap={24}
              pb={18}
            >
              <CpuCostChart costType={costType} />
              <MemoryCostChart costType={costType} />
              {hasGpuData && <GpuCostChart costType={costType} />}
              {showStorage && <StorageCostChart costType={costType} />}
            </Box>
          </Scroller>
        </TourStep>
      </ReportSection>
    </NormalizedCostProvider>
  );
};
